import React, {useMemo, useState} from "react";
import {Alert, Button, Spinner} from "react-bootstrap";
import {PiWarningCircle} from "react-icons/pi";
import SelectList from "../SelectList.jsx";
import useStatisticsData from "./useStatisticsData.js";
import {colorForGroup, formatNumber, OTHER_GROUP_COLOR} from "./format.js";

/**
 * Empfohlene Palettenstellplätze je Sortiment als Kachelübersicht.
 *
 * Der Zweck ist die Besprechung: Auf einen Blick soll ablesbar sein, welches
 * Produkt wie viele Stellplätze braucht, um lieferfähig zu bleiben. Deshalb
 * steht der Klartextname im Vordergrund und nicht die Artikelnummer - mit
 * "327000" kann am Tisch niemand etwas anfangen, mit "PREMIUM 400 ml" jeder.
 *
 * Die Zahl ist immer auf volle Paletten aufgerundet ("pallets_target_full"
 * aus der Rechnung): Ein Stellplatz lässt sich nicht teilen, wer 2,1 Paletten
 * braucht, belegt drei Plätze. Der ungerundete Wert steht klein daneben, damit
 * nachvollziehbar bleibt, wie knapp oder deutlich aufgerundet wurde.
 *
 * Diese Übersicht ist bewusst unabhängig von den Hallendarstellungen weiter
 * unten: Die Hallenmaße sind bislang Annahmen, der Stellplatzbedarf dagegen
 * folgt aus den echten Absatzdaten und bleibt gültig, egal wie die Hallen
 * am Ende vermessen werden.
 */

// Erste Anzeige bewusst begrenzt: Die vollständige Liste ist lang, und die
// ersten Kacheln tragen den größten Teil des Bedarfs. Der Rest ist einen
// Klick entfernt.
const ANFANGS_SICHTBAR = 24;

export default function LagerplaetzeStellplatzKacheln({rows, total, colorMap, unassignableCount = 0, assumptionParams}) {
    const [alleZeigen, setAlleZeigen] = useState(false);
    const [ebene, setEbene] = useState("parent");
    const [sortimentFilter, setSortimentFilter] = useState("");

    // Die Farbton-Ansicht wird erst geladen, wenn sie auch gebraucht wird -
    // die Rechnung über alle Einzelartikel ist deutlich aufwendiger als die
    // über die Sortimente.
    const {data: artikelDaten, loading: artikelLaedt, error: artikelFehler} = useStatisticsData(
        "/statistics/inventory-strategy/flaechenbedarf",
        {...(assumptionParams || {}), level: "article"},
        {skip: ebene !== "article" || !assumptionParams}
    );

    const istArtikelEbene = ebene === "article";
    const aktiveZeilen = istArtikelEbene ? artikelDaten?.rows || [] : rows || [];
    const aktiveSumme = istArtikelEbene ? artikelDaten?.total : total;

    // Auswahl der Sortimente für den Filter der Farbton-Ansicht: aus den
    // Sortimentszeilen, damit die Liste auch dann vollständig ist, wenn die
    // Artikelansicht noch lädt.
    const sortimentOptionen = useMemo(() => {
        const liste = (rows || [])
            .filter((row) => row.pallets_target_full > 0)
            .map((row) => ({
                value: row.parent_article_number,
                label: `${row.parent_name || row.name || row.parent_article_number}`,
            }))
            .sort((a, b) => a.label.localeCompare(b.label, "de"));

        return [{value: "", label: "Alle Sortimente"}, ...liste];
    }, [rows]);

    // Nur Zeilen mit bekannter Palettengröße und echtem Bedarf; absteigend
    // nach aufgerundetem Stellplatzbedarf, bei Gleichstand nach dem exakten
    // Wert - sonst wäre die Reihenfolge innerhalb einer Stufe zufällig.
    const kacheln = useMemo(() => {
        return aktiveZeilen
            .filter((row) => row.pallets_target_full !== null && row.pallets_target_full > 0)
            .filter((row) => {
                if (!istArtikelEbene || !sortimentFilter) {
                    return true;
                }
                return String(row.sortiment_article_number) === String(sortimentFilter);
            })
            .slice()
            .sort((a, b) => (b.pallets_target_full - a.pallets_target_full) || (b.pallets_target - a.pallets_target));
    }, [aktiveZeilen, istArtikelEbene, sortimentFilter]);

    const sichtbar = alleZeigen ? kacheln : kacheln.slice(0, ANFANGS_SICHTBAR);

    // Bei aktivem Sortimentsfilter zeigen die Summen nur die Auswahl - sonst
    // stünde über einer Handvoll Farbtöne die Gesamtsumme des ganzen Hauses.
    const gefiltert = istArtikelEbene && sortimentFilter !== "";
    const summeAufgerundet = gefiltert
        ? kacheln.reduce((s, r) => s + r.pallets_target_full, 0)
        : aktiveSumme?.pallets_target_full ?? kacheln.reduce((s, r) => s + r.pallets_target_full, 0);
    const summeRoh = gefiltert
        ? kacheln.reduce((s, r) => s + r.pallets_target, 0)
        : aktiveSumme?.pallets_target ?? 0;
    const groessteZahl = kacheln.length > 0 ? kacheln[0].pallets_target_full : 0;
    const einheitLang = istArtikelEbene ? "Farbtöne" : "Sortimente";

    // Vergleichswert derselben Auswahl auf Sortimentsebene. Er liegt immer
    // unter der Summe der Einzelfarben, und dieser Unterschied ist kein
    // Rechenfehler, sondern Risikoausgleich: Wer je Farbton plant, legt für
    // jeden einzelnen eine eigene Schwankungsreserve an; im Sortiment gleichen
    // sich starke und schwache Monate der Farben gegenseitig aus. Ohne diese
    // Gegenüberstellung wirken die beiden Ansichten widersprüchlich.
    const sortimentVergleich = useMemo(() => {
        if (!istArtikelEbene) {
            return null;
        }
        const quelle = rows || [];
        if (sortimentFilter) {
            const treffer = quelle.find((row) => String(row.parent_article_number) === String(sortimentFilter));
            return treffer ? treffer.pallets_target_full : null;
        }
        return total?.pallets_target_full ?? null;
    }, [istArtikelEbene, rows, sortimentFilter, total]);

    // Wie stark das Aufrunden die Gesamtzahl treibt, ist keine Nebensache:
    // Sortimente mit einem Bedarf von Bruchteilen einer Palette belegen in
    // dieser Rechnung jeweils einen ganzen Stellplatz. In der Praxis teilen
    // sie sich einen Mischplatz - wer die Gesamtsumme für die Hallenplanung
    // heranzieht, muss wissen, welcher Teil daher stammt.
    const kleinmengen = useMemo(() => kacheln.filter((row) => row.pallets_target < 1), [kacheln]);
    const kleinmengenAufschlag = useMemo(
        () => kleinmengen.reduce((summe, row) => summe + (1 - row.pallets_target), 0),
        [kleinmengen]
    );

    return (
        <section className="mb-4" aria-labelledby="stellplatz-kacheln-titel">
            <div className="d-flex flex-wrap align-items-baseline justify-content-between gap-2 mb-2">
                <h2 id="stellplatz-kacheln-titel" className="h5 mb-0">
                    Empfohlene Palettenstellplätze je {istArtikelEbene ? "Farbton" : "Produkt"}
                </h2>
                <div className="small text-muted">
                    {formatNumber(kacheln.length)} {einheitLang} · zusammen{" "}
                    <strong className="text-body">{formatNumber(summeAufgerundet)} Stellplätze</strong>
                </div>
            </div>

            {/* Umschalter Sortiment/Farbton. Innerhalb eines Sortiments liegen
                Welten zwischen den Farbtönen - ein Topseller braucht ein
                Vielfaches einer selten gefragten Farbe. */}
            <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                <div className="btn-group" role="group" aria-label="Auswertungsebene">
                    <Button
                        variant={istArtikelEbene ? "outline-secondary" : "secondary"}
                        size="sm"
                        onClick={() => { setEbene("parent"); setAlleZeigen(false); }}
                        aria-pressed={!istArtikelEbene}
                    >
                        Je Sortiment
                    </Button>
                    <Button
                        variant={istArtikelEbene ? "secondary" : "outline-secondary"}
                        size="sm"
                        onClick={() => { setEbene("article"); setAlleZeigen(false); }}
                        aria-pressed={istArtikelEbene}
                    >
                        Je Farbton
                    </Button>
                </div>

                {istArtikelEbene && (
                    <SelectList
                        value={sortimentFilter}
                        options={sortimentOptionen}
                        onChange={(wert) => { setSortimentFilter(wert); setAlleZeigen(false); }}
                        size="sm"
                        ariaLabel="Sortiment einschränken"
                        style={{minWidth: "16rem", maxWidth: "100%"}}
                    />
                )}

                {istArtikelEbene && artikelLaedt && (
                    <span className="d-inline-flex align-items-center gap-2 small text-muted">
                        <Spinner animation="border" size="sm" role="status" aria-hidden="true" />
                        Farbtöne werden gerechnet…
                    </span>
                )}
            </div>

            {artikelFehler && <Alert variant="danger">{artikelFehler}</Alert>}

            <p className="small text-muted mb-2">
                So viele volle Paletten braucht {istArtikelEbene ? "jeder einzelne Farbton" : "jedes Sortiment"} auf
                Lager, um zwischen zwei Bestellungen lieferfähig zu bleiben - Wiederbeschaffungszeit, Bestellzyklus und
                Schwankungsreserve eingerechnet. Immer aufgerundet, weil ein angebrochener Stellplatz im Regal
                trotzdem ein ganzer ist. Die Summe der aufgerundeten Werte liegt deshalb über der rechnerischen Summe
                von {formatNumber(summeRoh)} Paletten.
            </p>

            {kacheln.length > 0 && kleinmengen.length > 0 && (
                <p className="small text-muted mb-3">
                    <strong className="text-body">Beim Lesen der Gesamtzahl beachten:</strong>{" "}
                    {formatNumber(kleinmengen.length)} der {formatNumber(kacheln.length)} {einheitLang} brauchen
                    rechnerisch <em>weniger als eine ganze Palette</em> und belegen hier trotzdem je einen Stellplatz -
                    allein daraus stammen {formatNumber(Math.round(kleinmengenAufschlag))} der{" "}
                    {formatNumber(Math.round(summeAufgerundet - summeRoh))} Stellplätze Aufschlag. Die übrigen{" "}
                    {formatNumber(kacheln.length - kleinmengen.length)} {einheitLang} mit mindestens einer vollen
                    Palette kommen zusammen auf {formatNumber(summeAufgerundet - kleinmengen.length)} Stellplätze. Ob
                    sich Kleinstmengen im Lager einen Mischplatz teilen, ist eine betriebliche Entscheidung - diese
                    Rechnung trifft sie bewusst nicht.
                </p>
            )}

            {istArtikelEbene && sortimentVergleich !== null && summeAufgerundet > sortimentVergleich && (
                <Alert variant="secondary" className="py-2 small mb-3">
                    <strong className="text-body">Warum die Summe hier höher ist als „Je Sortiment":</strong> Dieselbe
                    Auswahl kommt auf Sortimentsebene auf {formatNumber(sortimentVergleich)} Stellplätze, je Farbton
                    gerechnet auf {formatNumber(summeAufgerundet)}. Das ist kein Widerspruch, sondern
                    Risikoausgleich: Wer jeden Farbton einzeln bevorratet, legt für jeden eine eigene
                    Schwankungsreserve an - im Sortiment gleichen sich starke und schwache Monate der einzelnen Farben
                    gegenseitig aus. <strong className="text-body">Für die Hallenplanung ist die Sortimentszahl die
                    realistischere</strong>, weil sich die Farben eines Sortiments Stellplätze teilen; diese Ansicht
                    hier zeigt, <em>welche</em> Farbtöne den Platz beanspruchen.
                </Alert>
            )}

            {kacheln.length === 0 && !artikelLaedt && (
                <Alert variant="secondary" className="py-2 small mb-0">
                    Für diese Auswahl gibt es keine Zeile mit bekannter Palettengröße und Bedarf.
                </Alert>
            )}

            <div className="stellplatz-kacheln">
                {sichtbar.map((row) => {
                    // Auf Farbton-Ebene ist "name" der Artikelname und
                    // "parent_name" das Sortiment, zu dem er gehört; auf
                    // Sortimentsebene trägt "parent_name" den Sortimentsnamen
                    // selbst und ist der bessere Titel.
                    const name = istArtikelEbene
                        ? row.name || row.parent_article_number
                        : row.parent_name || row.name || "Ohne Bezeichnung";
                    const zugehoerigkeit = istArtikelEbene ? row.parent_name : null;
                    const farbe = colorForGroup(colorMap, row.article_group) || OTHER_GROUP_COLOR;
                    // Anteil an der größten Kachel - macht die Größenverhältnisse
                    // ohne zusätzliches Diagramm ablesbar.
                    const anteil = groessteZahl > 0 ? (row.pallets_target_full / groessteZahl) * 100 : 0;
                    const belegt = row.pallets_current;

                    return (
                        <article
                            key={row.color_key || row.parent_article_number}
                            className="stellplatz-kachel"
                            style={{borderTopColor: farbe}}
                        >
                            <div className="stellplatz-kachel-kopf">
                                <h3 className="stellplatz-kachel-name" title={name}>{name}</h3>
                                <div className="stellplatz-kachel-nummer">
                                    {row.parent_article_number}
                                    {row.article_count > 1 && (
                                        <> · {formatNumber(row.article_count)} Artikel</>
                                    )}
                                    {zugehoerigkeit && (
                                        <span className="stellplatz-kachel-sortiment" title={zugehoerigkeit}>
                                            {zugehoerigkeit}
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className="stellplatz-kachel-zahl">
                                <span className="stellplatz-kachel-wert">{formatNumber(row.pallets_target_full)}</span>
                                <span className="stellplatz-kachel-einheit">
                                    {row.pallets_target_full === 1 ? "Stellplatz" : "Stellplätze"}
                                </span>
                            </div>

                            <div className="stellplatz-kachel-balken" aria-hidden="true">
                                <span style={{width: `${Math.max(anteil, 2)}%`, backgroundColor: farbe}} />
                            </div>

                            <dl className="stellplatz-kachel-fuss">
                                <div>
                                    <dt>rechnerisch</dt>
                                    <dd>{formatNumber(row.pallets_target)} Pal.</dd>
                                </div>
                                <div>
                                    <dt>aktuell belegt</dt>
                                    <dd>{belegt === null || belegt === undefined ? "–" : `${formatNumber(belegt)} Pal.`}</dd>
                                </div>
                            </dl>
                        </article>
                    );
                })}
            </div>

            {kacheln.length > ANFANGS_SICHTBAR && (
                <div className="mt-3">
                    <Button variant="outline-secondary" size="sm" onClick={() => setAlleZeigen((v) => !v)}>
                        {alleZeigen
                            ? `Nur die ${ANFANGS_SICHTBAR} größten zeigen`
                            : `Alle ${formatNumber(kacheln.length)} ${einheitLang} zeigen`}
                    </Button>
                </div>
            )}

            {!istArtikelEbene && unassignableCount > 0 && (
                <Alert variant="secondary" className="d-flex align-items-start gap-2 mt-3 mb-0 py-2">
                    <PiWarningCircle size={18} className="flex-shrink-0 mt-1" aria-hidden="true" />
                    <div className="small">
                        Für {formatNumber(unassignableCount)} weitere Sortimente ist keine Palettengröße hinterlegt -
                        ihr Bedarf lässt sich nur in Stück ausweisen und fehlt deshalb in dieser Übersicht. Die
                        Stückzahl je Palette wird in der Rechnung je Warengruppe gepflegt.
                    </div>
                </Alert>
            )}
        </section>
    );
}
